import React, { FC, useState } from "react";
import { Form, Modal, Button, Alert, Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import * as yup from "yup";
import ClipLoader from "react-spinners/ClipLoader";
import { CoupleType, PersonType } from "../../../../types/PersonType";
import { PersonModel } from "../../../../models/PersonModel";
import { isResponseError, OptionType, ResponseErrorType } from "../../../../types/CommonType";
import TranslateTerms from "../../../../components/TranslateTerms";
import { FormattedMessage, useIntl } from "react-intl";
import { LANG_COMMON_CANCEL, LANG_COMMON_DIAGNOSIS, LANG_COMMON_EVOLUTION, LANG_COMMON_LEGAL_ID, LANG_COMMON_LOADING, LANG_COMMON_MEDICAL_RECORD_NUMBER, LANG_COMMON_NAME, LANG_COMMON_NEW, LANG_COMMON_PATIENT, LANG_COMMON_SAVE, LANG_ERROR, LANG_WARNING_FILL_ALL } from "../../../../lang";
import InputMask from 'react-input-mask';
import clsx from "clsx";
import { CoupleModel } from "../../../../models/CoupleModel";
import { MedicalRecordEvolutionType, MedicalRecordType } from "../../../../types/MedicalRecordType";
import { MedicalRecordModel } from "../../../../models/MedicalRecordModel";
import { DefaultUser } from "../../../../providers/DefaultData";
import { BtnBold, BtnBulletList, BtnItalic, BtnNumberedList, BtnStrikeThrough, BtnUnderline, BtnUndo, Editor, EditorProvider, Separator, Toolbar } from "react-simple-wysiwyg";
import { TreatmentMedicationType, TreatmentType } from "../../../../types/TreatmentType";
import { TreatmentModel } from "../../../../models/TreatmentModel";
import CustomSelect from "../../../../components/form/CustomSelect";
import moment from "moment";
import TreatmentMedications from "./TreatmentMedications";
import TreatmentMedicationModal from "./TreatmentMedicationModal";

interface Props {
    show: boolean
    onHide: () => void
    onSave: (treatment: TreatmentType) => void
    treatment: TreatmentType | null
    medical_record: MedicalRecordType
}

const TreatmentAddModal: FC<Props> = ({ show, onHide, onSave, treatment, medical_record }) => {
    const schema = yup.object().shape({
        type: yup.string().required(),
        origin_semen: yup.string().required(),
        origin_ovum: yup.string().required(),
        eoc_date: yup.string().required(),
        dum_date: yup.string().required()
    });
    const [formWasSubmitted, setFormWasSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const intl = useIntl();
    const DefaultErrorMsg = intl.formatMessage(LANG_ERROR);
    const [errorMsg, setErrorMsg] = useState(DefaultErrorMsg);
    const [medications, setMedications] = useState<TreatmentMedicationType[]>((treatment?.treatment_medications) ? treatment.treatment_medications : []);
    const [medicationsTrigger, setMedicationsTrigger] = useState<TreatmentMedicationType[]>((treatment?.trigger_medications) ? treatment.trigger_medications : []);
    const [showAddMedicationModal, setShowAddMedicationModal] = useState(false);
    const [medicationToAdd, setMedicationToAdd] = useState<TreatmentMedicationType | null>(null);

    const [showEditMedicationModal, setShowEditMedicationModal] = useState(false);
    const [medicationToEdit, setMedicationToEdit] = useState<TreatmentMedicationType | null>(null);
    const [medicationToEditIndex, setMedicationToEditIndex] = useState<number | null>(null);

    const initialTreatment: TreatmentType = (treatment) ?
        {
            ...treatment,
            puncture_date: (treatment.puncture_date) ? moment(treatment.puncture_date).format("YYYY-MM-DD") : ""
        }
        : {
            id: -1,
            type: "",
            origin_semen: "",
            origin_ovum: "",
            medications: [],
            created_at: "",
            medical_record_id: medical_record.id,
            eoc_date: "",
            dum_date: ""
        };

    const scrollToTop = () => {
        // Scroll to top
        document.querySelector('div[role="dialog"]')?.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    const onSubmit = async (values: TreatmentType) => {
        if (medications.length === 0 && medicationsTrigger.length === 0) {
            setHasError(true);
            setErrorMsg("Adicione ao menos uma medicação.");
            scrollToTop();
            return;
        }

        setLoading(true);
        setFormWasSubmitted(false);
        setErrorMsg(DefaultErrorMsg);
        setHasError(false);

        // Create or update
        let resp: TreatmentType | ResponseErrorType = (treatment === null) ?
            await TreatmentModel().create({
                ...values,
                medications: [...medications, ...medicationsTrigger]
            }, medical_record.id)
            : await TreatmentModel().update(treatment.id, {
                ...values,
                medications: [...medications, ...medicationsTrigger]
            });
        setLoading(false);
        // Saving result
        if (!isResponseError(resp)) {
            onSave(resp as TreatmentType);
        } else {
            setHasError(true);
            setErrorMsg((resp as ResponseErrorType).message);
            scrollToTop();
        }
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();

        const clipboard = e.clipboardData;
        const htmlData = clipboard.getData('text/html');
        const plainText = clipboard.getData('text/plain');

        let cleanText = '';

        if (htmlData) {
            const parsed = new DOMParser().parseFromString(htmlData, 'text/html');
            cleanText = parsed.body.textContent || '';
        } else {
            cleanText = plainText;
        }

        // Substitui quebras de linha por <br> para manter no editor
        const textWithLineBreaks = cleanText.replace(/\n/g, '<br>');

        // Insere como HTML agora, não como texto puro
        document.execCommand('insertHTML', false, textWithLineBreaks);
    };

    return (
        <>
            <Modal
                show={!showAddMedicationModal && !showEditMedicationModal && show}
                onHide={() => {
                    onHide();
                    setFormWasSubmitted(false);
                }}
                className="modal-default"
                size="xl"
                backdrop="static"

            >
                <Formik
                    validationSchema={schema}
                    onSubmit={onSubmit}
                    initialValues={initialTreatment}
                >
                    {({
                        handleSubmit,
                        handleChange,
                        values,
                        touched,
                        setFieldValue,
                        isValid,
                        errors }) => (
                        <Form noValidate onSubmit={(e) => {
                            setFormWasSubmitted(true);
                            handleSubmit(e);
                        }}>
                            <Modal.Header closeButton closeVariant="white">
                                <Modal.Title>
                                    Tratamento
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                {(!isValid && formWasSubmitted) && (<Alert variant={'danger'}><FormattedMessage {...LANG_WARNING_FILL_ALL} /></Alert>)}
                                {hasError && (<Alert variant={'danger'}>{errorMsg}</Alert>)}
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" controlId="name">
                                            <Form.Label>Tipo de tratamento</Form.Label>
                                            <CustomSelect
                                                options={TreatmentModel().getTreatmentTypeOptions()}
                                                name="type"
                                                onChange={(o: OptionType) => {
                                                    setFieldValue('type', o.value);
                                                }}
                                                placeholder="Selecione..."
                                                value={values.type}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg={4}>
                                        <Form.Group className="mb-3" controlId="name">
                                            <Form.Label>Origem do Sêmen</Form.Label>
                                            <CustomSelect
                                                options={TreatmentModel().getSemenOriginOptions()}
                                                name="origin_semen"
                                                onChange={(o: OptionType) => {
                                                    setFieldValue('origin_semen', o.value);
                                                }}
                                                placeholder="Selecione..."
                                                value={values.origin_semen}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col lg={4}>
                                        <Form.Group className="mb-3" controlId="name">
                                            <Form.Label>Origem dos Óvulos</Form.Label>
                                            <CustomSelect
                                                options={TreatmentModel().getOvumOriginOptions()}
                                                name="origin_ovum"
                                                onChange={(o: OptionType) => {
                                                    setFieldValue('origin_ovum', o.value);
                                                }}
                                                placeholder="Selecione..."
                                                value={values.origin_ovum}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col lg={4}>
                                        <Form.Group className="mb-3" controlId="name">
                                            <Form.Label>Data de início {TreatmentModel().isTreatmentTypeTE(values.type) ? "PE" : "EOC"}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="eoc_date"
                                                value={values.eoc_date}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col lg={4}>
                                        <Form.Group className="mb-3" controlId="name">
                                            <Form.Label>Data da última menstruação</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="dum_date"
                                                value={values.dum_date}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" controlId="name">
                                            <Form.Label>Outras informações</Form.Label>
                                            <EditorProvider>
                                                <Editor
                                                    containerProps={{
                                                        onPaste: handlePaste,
                                                    }}
                                                    name="content"
                                                    placeholder="Outras informações"
                                                    value={values.other_info}
                                                    onChange={(e) => {
                                                        setFieldValue('other_info', e.target.value);
                                                    }}
                                                >
                                                    <Toolbar>
                                                        <BtnBold />
                                                        <BtnItalic />
                                                        <BtnUnderline />
                                                        <BtnStrikeThrough />
                                                        <Separator />
                                                        <BtnNumberedList />
                                                        <BtnBulletList />
                                                    </Toolbar>
                                                </Editor>
                                            </EditorProvider>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <TreatmentMedications
                                    medications={medications}
                                    treatment={treatment}
                                    type="TREAT"
                                    treatmentType={values.type}
                                    onChange={(med) => {
                                        setMedications(med);
                                    }}
                                    onEditClick={(index) => {
                                        setMedicationToEdit(medications[index]);
                                        setMedicationToEditIndex(index);
                                        setShowEditMedicationModal(true);
                                    }}
                                />
                                <Col lg={12}>
                                    <Form.Group className="mb-3" controlId="name">
                                        <Button variant="primary" className="btn-custom bg-custom-gradient"
                                            onClick={() => {
                                                setShowAddMedicationModal(true);
                                                setMedicationToAdd({
                                                    id: -1,
                                                    medication: '',
                                                    treatment_id: (treatment) ? treatment.id : -1,
                                                    date_start: '',
                                                    date_end: '',
                                                    start_time: '',
                                                    dosage: 0,
                                                    posology: "",
                                                    type: "TREAT"
                                                });
                                            }}
                                        >
                                            Adicionar medicação
                                        </Button>
                                    </Form.Group>
                                </Col>
                                <TreatmentMedications
                                    medications={medicationsTrigger}
                                    treatment={treatment}
                                    type="TRIGG"
                                    treatmentType={values.type}
                                    onChange={(med) => {
                                        setMedicationsTrigger(med);
                                    }}
                                    onEditClick={(index) => {
                                        setMedicationToEdit(medicationsTrigger[index]);
                                        setMedicationToEditIndex(index);
                                        setShowEditMedicationModal(true);
                                    }}
                                />
                                <Col lg={12}>
                                    <Form.Group className="mb-3" controlId="name">
                                        <Button variant="primary" className="btn-custom bg-custom-gradient"
                                            onClick={() => {
                                                setShowAddMedicationModal(true);
                                                setMedicationToAdd({
                                                    id: -1,
                                                    medication: '',
                                                    treatment_id: (treatment) ? treatment.id : -1,
                                                    date_start: '',
                                                    date_end: '',
                                                    start_time: '',
                                                    dosage: 0,
                                                    posology: "",
                                                    type: "TRIGG"
                                                });
                                            }}
                                        >
                                            Adicionar medicação
                                        </Button>
                                    </Form.Group>
                                </Col>
                                <Row>
                                    <Col lg={12}>
                                        <div className="title-divider">
                                            <h6>{TreatmentModel().isTreatmentTypeTE(values.type) ? "TRANSFERÊNCIA" : "PUNÇÃO"}</h6>
                                            <hr />
                                        </div>
                                    </Col>
                                    <Col lg={3}>
                                        <Form.Group className="mb-3" controlId="name">
                                            <Form.Label>Data</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="puncture_date"
                                                value={values.puncture_date}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col lg={3}>
                                        <Form.Group className="mb-3" controlId="name">
                                            <Form.Label>Horário</Form.Label>
                                            <InputMask
                                                value={values.puncture_time}
                                                onChange={handleChange}
                                                name={"puncture_time"}
                                                type="text"
                                                mask="99:99"
                                                className={clsx("form-control", {
                                                    // "is-invalid": formik.touched.cpf && formik.errors.cpf,
                                                    //"is-valid": !formik.errors.cpf && formik.touched.cpf
                                                })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col lg={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Laboratório</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="puncture_clinic"
                                                placeholder="Laboratório"
                                                value={values.puncture_clinic}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="outline-light" onClick={onHide} className="btn-custom btn-custom-light">
                                    <FormattedMessage {...LANG_COMMON_CANCEL} />
                                </Button>
                                <Button variant="primary" type={"submit"} disabled={loading} className="btn-custom bg-custom-gradient">
                                    {loading ? <><ClipLoader color={'#ffffff'} loading={loading} size={'12px'} /> <FormattedMessage {...LANG_COMMON_LOADING} /></> : <FormattedMessage {...LANG_COMMON_SAVE} />}
                                </Button>
                            </Modal.Footer>
                        </Form>
                    )}
                </Formik>
            </Modal>
            {showAddMedicationModal && medicationToAdd && (
                <TreatmentMedicationModal
                    treatmentMedication={medicationToAdd}
                    onSave={(medication) => {
                        setMedications([...medications, medication]);
                        setShowAddMedicationModal(false);
                        setMedicationToAdd(null);
                    }}
                    onCancel={() => {
                        setShowAddMedicationModal(false);
                        setMedicationToAdd(null);
                    }}
                />
            )}
            {showEditMedicationModal && medicationToEdit !== null && (
                <TreatmentMedicationModal
                    treatmentMedication={medicationToEdit}
                    onSave={(medication) => {
                        const updatedMedications = medication.type === 'TREAT'
                            ? [...medications]
                            : [...medicationsTrigger];
                        if (medicationToEditIndex !== null && medicationToEditIndex >= 0 && medicationToEditIndex < updatedMedications.length) {
                            updatedMedications[medicationToEditIndex] = medication;
                        } else {
                            // fallback: if index is not set, append the medication
                            updatedMedications.push(medication);
                        }
                        if (medication.type === 'TREAT') {
                            setMedications(updatedMedications);
                        } else {
                            setMedicationsTrigger(updatedMedications);
                        }
                        setShowEditMedicationModal(false);
                        setMedicationToEditIndex(null);
                    }}
                    onCancel={() => {
                        setShowEditMedicationModal(false);
                        setMedicationToEditIndex(null);
                    }}
                />
            )}
        </>
    )
}

export default TreatmentAddModal;